import Link from 'next/link';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { lostGroupKeys, type LostGroupState as LostState } from '../../../lib/group-chat-actions';

/**
 * A group the server refused this viewer for (NOT_A_MEMBER / GROUP_ENDED): "You're not in this group", "You were
 * removed from this group" or "This group has ended", with a way back to the chats — in place of the transcript and
 * its composer, never a private chat. iOS `LostGroupStateView` and Android `LostGroupState` draw the same.
 */
export function LostGroupState({ lost, narrow }: { lost: LostState; narrow: boolean }) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const keys = lostGroupKeys(lost);
  return (
    <div data-testid="group-lost-state" data-lost={lost} style={{ ...WRAP, padding: narrow ? '40px 24px 56px' : '64px 48px 80px' }}>
      <div style={{ ...TITLE, fontSize: narrow ? 22 : 26 }}>{t(keys.title)}</div>
      <div style={BODY}>{t(keys.body)}</div>
      <Link href="/chats" as={`${basePath}/chats`} style={BACK_LINK} data-testid="group-lost-back">
        {t('chat.group.lost.back')}
      </Link>
    </div>
  );
}

const WRAP: React.CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 20,
  textAlign: 'center',
};

const TITLE: React.CSSProperties = {
  fontFamily: 'var(--brand-font-display)',
  fontWeight: 400,
  color: 'rgba(var(--ink-rgb), 0.85)',
  lineHeight: 1.25,
};

const BODY: React.CSSProperties = {
  fontSize: 14,
  color: 'rgba(var(--ink-rgb), 0.5)',
  lineHeight: 1.55,
  maxWidth: 440,
};

const BACK_LINK: React.CSSProperties = {
  marginTop: 6,
  padding: '10px 18px',
  borderRadius: 'var(--sym-radius-button)',
  fontSize: 13,
  fontWeight: 'var(--sym-weight-medium)',
  textDecoration: 'none',
  color: 'var(--sym-on-accent)',
  background: 'var(--sym-accent)',
};
